// import type {
//   CoveredMethod,
//   NotCoveredMethod,
// } from '@/components/smartai/api/testIntelligence';

// const COLUMNS = [
//   'Method',
//   'FilePath',
//   'ChangeType',
//   'Status',
//   'TestCaseId',
//   'TestCaseTitle',
//   'Similarity(%)',
// ];

// function escapeCsv(value: string | number | undefined): string {
//   const str = value === undefined || value === null ? '' : String(value);
//   if (str.includes(',') || str.includes('"') || str.includes('\n')) {
//     return `"${str.replace(/"/g, '""')}"`;
//   }
//   return str;
// }

// function buildRows(
//   covered: CoveredMethod[],
//   notCovered: NotCoveredMethod[],
// ): string[][] {
//   const rows: string[][] = [];

//   for (const item of covered) {
//     const cases = item.matchingAdoTestCases;
//     if (cases.length === 0) {
//       rows.push([
//         item.method,
//         item.filePath,
//         item.changeType,
//         'MAPPED',
//         '',
//         '',
//         '',
//       ]);
//     } else {
//       cases.forEach((tc, i) =>
//         rows.push([
//           i === 0 ? item.method : '',
//           i === 0 ? item.filePath : '',
//           i === 0 ? item.changeType : '',
//           i === 0 ? 'MAPPED' : '',
//           String(tc.testCaseId),
//           tc.title,
//           (tc.similarity * 100).toFixed(2),
//         ]),
//       );
//     }
//   }

//   for (const item of notCovered) {
//     const cases = item.suggestedTestCases;
//     if (cases.length === 0) {
//       rows.push([
//         item.method,
//         item.filePath,
//         item.changeType,
//         'NOT MAPPED',
//         '',
//         '',
//         '',
//       ]);
//     } else {
//       cases.forEach((tc, i) =>
//         rows.push([
//           i === 0 ? item.method : '',
//           i === 0 ? item.filePath : '',
//           i === 0 ? item.changeType : '',
//           i === 0 ? 'NOT MAPPED' : '',
//           String(tc.testCaseId),
//           tc.title,
//           (tc.similarity * 100).toFixed(2),
//         ]),
//       );
//     }
//   }

//   return rows;
// }

// export function downloadCsv(
//   covered: CoveredMethod[],
//   notCovered: NotCoveredMethod[],
//   fileName: string,
// ): void {
//   const header = COLUMNS.map(escapeCsv).join(',');
//   const body = buildRows(covered, notCovered)
//     .map(row => row.map(escapeCsv).join(','))
//     .join('\n');

//   const csv = `${header}\n${body}`;
//   const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
//   const url = URL.createObjectURL(blob);
//   const anchor = document.createElement('a');
//   anchor.href = url;
//   anchor.download = `${fileName || 'test-mapping'}.csv`;
//   document.body.appendChild(anchor);
//   anchor.click();
//   document.body.removeChild(anchor);
//   URL.revokeObjectURL(url);
// }
import type {
  CoveredMethod,
  NotCoveredMethod,
} from '@/components/smartai/api/testIntelligence';
import xlsx from 'json-as-xlsx';

interface NotCoveredItem {
  method: string;
  filePath: string;
  module: string;
  changeType: string;
  status: string;
  suggestedTestCases: SuggestedTestCase[];
}

interface MatchingAdoTestCases {
  testCaseId: number;
  title: string;
  similarity: number;
  steps: string;
}

interface CoveredItem {
  method: string;
  filePath: string;
  module: string;
  changeType: string;
  status: string;
  matchingAdoTestCases: MatchingAdoTestCases[];
  generatedTestCases: CoveredSuggestedTestCase[];
}

interface SuggestedTestCase {
  title: string;
  priority: string;
  similarity: number;
  confidence: string;
  steps: string;
}

interface CoveredSuggestedTestCase extends SuggestedTestCase {
  type?: string;
  module?: string;
  preconditions?: string;
  bestMatch?: {
    testCaseId: number;
    title: string;
    similarity: number;
    relevant: boolean;
  };
}

interface SheetColumn {
  label: string;
  value: string;
}

interface SheetData {
  sheet: string;
  columns: SheetColumn[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: Record<string, any>[];
}

function formatSimilarity(similarity: number | null | undefined): string {
  if (similarity == null) return '';
  return `${(similarity * 100).toFixed(1)}%`;
}

function convertApiResponseToSheet(
  notCovered: NotCoveredItem[],
  covered: CoveredItem[],
  sheetName: string = 'Test_gap_analysis',
): SheetData {
  const columns: SheetColumn[] = [
    { label: 'Method', value: 'method' },
    { label: 'File Path', value: 'filePath' },
    { label: 'Change Type', value: 'changeType' },
    { label: 'Status', value: 'status' },
    { label: 'Test Case ID', value: 'matchedTestCaseId' },
    { label: 'Test Case - ADO', value: 'matchedTestCase' },
    { label: 'Test Case - AI', value: 'suggestedTestCase' },
    { label: 'Steps - ADO', value: 'matchedSteps' },
    { label: 'Steps - AI', value: 'suggestedSteps' },
    { label: 'Similarity', value: 'suggestedSimilarity' },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const content: Record<string, any>[] = [];

  // Process COVERED items
  for (const item of covered) {
    const baseRow = {
      method: item.method,
      filePath: item.filePath,
      module: item.module,
      changeType: item.changeType,
      status: item.status === 'COVERED' && 'MAPPED',
    };

    const matchedCount = item.matchingAdoTestCases?.length || 0;
    const suggestedCount = item.generatedTestCases?.length || 0;
    const maxRows = matchedCount + suggestedCount;

    for (let i = 0; i < maxRows; i++) {
      const matched = item.matchingAdoTestCases?.[i];
      const suggested = item.generatedTestCases?.[i];

      content.push({
        method: i === 0 ? baseRow.method : '',
        filePath: i === 0 ? baseRow.filePath : '',
        module: i === 0 ? baseRow.module : '',
        changeType: i === 0 ? baseRow.changeType : '',
        status: i === 0 ? baseRow.status : '',
        matchedTestCaseId: matched ? String(matched.testCaseId) : '',
        matchedTestCase: matched?.title || '',
        matchedSimilarity: matched ? formatSimilarity(matched.similarity) : '',
        matchedSteps: matched?.steps || '',
        suggestedTestCase: suggested?.title || '',
        suggestedSteps: suggested?.steps || '',
        priority: suggested?.priority || '',
        suggestedSimilarity: suggested
          ? formatSimilarity(suggested.similarity)
          : '',
        confidence: suggested?.confidence || '',
      });
    }
  }

  // Process NOT COVERED items
  for (const item of notCovered) {
    const baseRow = {
      method: item.method,
      filePath: item.filePath,
      module: item.module,
      changeType: item.changeType,
      status: item.status === 'NOT COVERED' && 'NOT MAPPED',
    };

    if (item.suggestedTestCases && item.suggestedTestCases.length > 0) {
      for (let i = 0; i < item.suggestedTestCases.length; i++) {
        const testCase = item.suggestedTestCases[i];
        content.push({
          method: i === 0 ? baseRow.method : '',
          filePath: i === 0 ? baseRow.filePath : '',
          module: i === 0 ? baseRow.module : '',
          changeType: i === 0 ? baseRow.changeType : '',
          status: i === 0 ? baseRow.status : '',
          matchedTestCaseId: '',
          matchedTestCase: '',
          matchedSimilarity: '',
          matchedSteps: '',
          suggestedTestCase: testCase.title,
          suggestedSteps: testCase.steps || '',
          priority: testCase.priority || '',
          suggestedSimilarity: formatSimilarity(testCase.similarity),
          confidence: testCase.confidence || '',
        });
      }
    } else {
      content.push({
        ...baseRow,
        matchedTestCaseId: '',
        matchedTestCase: '',
        matchedSimilarity: '',
        matchedSteps: '',
        suggestedTestCase: '',
        suggestedSteps: '',
        priority: '',
        suggestedSimilarity: '',
        confidence: '',
      });
    }
  }

  return {
    sheet: sheetName,
    columns,
    content,
  };
}

const downloadCsv = (
  covered: CoveredMethod[],
  notCovered: NotCoveredMethod[],
  fileName: string,
) => {
  const { sheet, columns, content } = convertApiResponseToSheet(
    notCovered,
    covered,
  );

  const data = [
    {
      sheet,
      columns,
      content,
    },
  ];

  const settings = {
    fileName: fileName,
    extraLength: 1,
    writeMode: 'writeFile' as const,
    writeOptions: {},
    RTL: false,
  };

  xlsx(data, settings);
};

export default downloadCsv;
